import { ComponentRef, ReactElement, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Checkbox,
  ErrorSummary,
  Fieldset,
  Field,
  Heading,
  Input,
  Label,
  Link,
  Paragraph,
  Radio,
  Select,
  Skeleton,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import { PadlockLockedIcon } from '@navikt/aksel-icons';
import type { PlanDetail, PlanOptions } from '../../../api/safetyRounds';
import { Routes } from '../../../enums/Routes';
import { useGetUserinfo } from '../../../hooks/queries/useGetUserinfo';
import { UserRoles } from '../../../api/queries';
import {
  useCreatePlanMutation,
  usePlanOptionsQuery,
  usePlanQuery,
  useSetPlanActiveMutation,
  useUpdatePlanMutation,
} from '../../../hooks/queries/safetyRounds/usePlanQueries';
import {
  FREQUENCY_OPTIONS,
  formToInput,
  isSafetyRepWithoutRole,
  newPlanForm,
  PLAN_FIELD_IDS,
  type PlanForm,
  type PlanFormError,
  planChecklistPath,
  planFormErrors,
  plansHomeFor,
  planToForm,
  toggleParticipant,
} from './plansView';
import { ApiErrorUtils } from '../../../utils/api-error.utils';
import { LoadProblem } from '../LoadProblem';
import layout from '../../HRDashboard/hmsPage.module.css';
import classes from './Plans.module.css';

/** «Ny vernerunde» and «Endre plan» */
export const PlanFormPage = (): ReactElement => {
  const { t } = useTranslation();
  const { planId } = useParams();
  const id = planId ? Number(planId) : null;
  const options = usePlanOptionsQuery();
  const plan = usePlanQuery(id);
  const loading = options.isLoading || (id !== null && plan.isLoading);
  const failed = options.error || !options.data || (id !== null && (plan.error || !plan.data));
  const home = plansHomeFor(useLocation().pathname);

  // Without access, or for a plan that does not exist, the page is not shown as an empty form
  const problem = options.error ?? (id !== null ? plan.error : null);
  if (ApiErrorUtils.pageProblemOf(problem)) return <LoadProblem error={problem} subject="plan" />;

  return (
    <div className={layout.page} data-size="md">
      <section className={layout.hero}>
        <div aria-hidden="true" className={layout.backdrop} />
        <div className={layout.heroContent}>
          <Link asChild data-size="sm" className={layout.startAligned}>
            <RouterLink to={home}>{t('safetyRounds.plan.back')}</RouterLink>
          </Link>
          <span className={layout.eyebrow}>{t('safetyRounds.plans.title')}</span>
          <Heading level={1} data-size="xl">
            {id === null ? t('safetyRounds.plan.newTitle') : (plan.data?.name ?? t('safetyRounds.plan.editTitle'))}
          </Heading>
          <Paragraph className={layout.intro}>{t('safetyRounds.plan.intro')}</Paragraph>
        </div>
      </section>
      {loading ? (
        <div aria-busy="true" className={layout.loading}>
          <span className="ds-sr-only">{t('safetyRounds.plan.loading')}</span>
          <Skeleton variant="rectangle" width="100%" height={420} />
        </div>
      ) : failed ? (
        <LoadProblem error={problem} subject="plan" />
      ) : (
        <Form key={id ?? 'new'} options={options.data!} plan={plan.data ?? null} />
      )}
    </div>
  );
};

const Form = ({ options, plan }: { options: PlanOptions; plan: PlanDetail | null }): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const home = plansHomeFor(useLocation().pathname);
  const [form, setForm] = useState<PlanForm>(plan ? planToForm(plan) : newPlanForm(options));
  const [errors, setErrors] = useState<PlanFormError[] | null>(null);
  const summaryRef = useRef<ComponentRef<typeof ErrorSummary>>(null);
  const create = useCreatePlanMutation();
  const update = useUpdatePlanMutation(plan?.id ?? 0);
  const archive = useSetPlanActiveMutation(plan?.id ?? 0);
  const save = plan ? update : create;
  const K = 'safetyRounds.plan';
  // A plan keeps an archived template, which is not offered for new plans
  const templates =
    plan && !options.templates.some((template) => template.id === plan.templateId)
      ? [
          ...options.templates,
          { id: plan.templateId, name: plan.template.name, description: '', version: plan.template.version, checkpoints: plan.checkpoints },
        ]
      : options.templates;
  const chosen = templates.find((template) => template.id === form.templateId);
  const safetyRepIds = options.safetyReps.map((rep) => rep.userId);
  const isHr = (useGetUserinfo().data?.userRoles ?? []).includes(UserRoles.HR_ADMIN);
  // The plan's safety representative has lost the role, or was chosen before only those with it were offered
  const safetyRepWithoutRole =
    plan !== null && plan.safetyRepHasRole === false && form.safetyRepId === plan.safetyRepId ? plan.safetyRepEmail : null;

  useEffect(() => {
    if (errors?.length) summaryRef.current?.focus();
  }, [errors]);

  const errorFor = (fieldId: string) => errors?.find((error) => error.fieldId === fieldId)?.message;

  const submit = () => {
    const found = planFormErrors(form, options.choosesSafetyRep, t, safetyRepIds);
    setErrors(found);
    if (found.length > 0) return;
    save.mutate(formToInput(form, options.choosesSafetyRep), {
      onSuccess: () => navigate(home),
    });
  };

  return (
    <div className={layout.layout}>
      <div className={layout.main}>
        <section aria-labelledby="plan-what" className={layout.card} data-gap="md">
          <Heading level={2} data-size="sm" id="plan-what">
            {t(`${K}.what`)}
          </Heading>
          <Field>
            <Label htmlFor={PLAN_FIELD_IDS.name}>{t(`${K}.name`)}</Label>
            <Input
              id={PLAN_FIELD_IDS.name}
              value={form.name}
              aria-invalid={errorFor(PLAN_FIELD_IDS.name) ? true : undefined}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
            {errorFor(PLAN_FIELD_IDS.name) && <ValidationMessage>{errorFor(PLAN_FIELD_IDS.name)}</ValidationMessage>}
          </Field>
          <Field>
            <Label htmlFor={PLAN_FIELD_IDS.area}>{t(`${K}.area`)}</Label>
            <Paragraph data-size="sm" className={layout.subtle} id="plan-area-description">
              {t(`${K}.areaDescription`)}
            </Paragraph>
            <Input
              id={PLAN_FIELD_IDS.area}
              value={form.area}
              aria-describedby="plan-area-description"
              aria-invalid={errorFor(PLAN_FIELD_IDS.area) ? true : undefined}
              onChange={(event) => setForm({ ...form, area: event.target.value })}
            />
            {errorFor(PLAN_FIELD_IDS.area) && <ValidationMessage>{errorFor(PLAN_FIELD_IDS.area)}</ValidationMessage>}
          </Field>
          <Field>
            <Label htmlFor={PLAN_FIELD_IDS.template}>{t(`${K}.template`)}</Label>
            {chosen && (
              <Paragraph data-size="sm" className={layout.subtle}>
                {chosen.description || t(`${K}.templateCount`, { count: chosen.checkpoints })}
              </Paragraph>
            )}
            <Select
              id={PLAN_FIELD_IDS.template}
              value={form.templateId ?? ''}
              aria-invalid={errorFor(PLAN_FIELD_IDS.template) ? true : undefined}
              onChange={(event) =>
                setForm({ ...form, templateId: event.target.value ? Number(event.target.value) : null })
              }
            >
              <Select.Option value="">{t(`${K}.chooseTemplate`)}</Select.Option>
              {templates.map((template) => (
                <Select.Option key={template.id} value={template.id}>
                  {t(`${K}.templateOption`, { name: template.name, version: template.version })}
                </Select.Option>
              ))}
            </Select>
            {errorFor(PLAN_FIELD_IDS.template) && (
              <ValidationMessage>{errorFor(PLAN_FIELD_IDS.template)}</ValidationMessage>
            )}
          </Field>
          {plan?.mayCustomize && (
            <div className={classes.callout}>
              <Paragraph data-size="sm">
                {t(`${K}.customized`, { count: plan.checkpoints, hidden: plan.hiddenCount, extra: plan.extraCount })}
              </Paragraph>
              <Button asChild data-size="sm" variant="secondary">
                <RouterLink to={planChecklistPath(plan.id)}>{t(`${K}.customize`)}</RouterLink>
              </Button>
            </div>
          )}
          {options.choosesSafetyRep && (
            <Field>
              <Label htmlFor={PLAN_FIELD_IDS.safetyRep}>{t(`${K}.safetyRep`)}</Label>
              <Select
                id={PLAN_FIELD_IDS.safetyRep}
                value={form.safetyRepId ?? ''}
                aria-invalid={errorFor(PLAN_FIELD_IDS.safetyRep) ? true : undefined}
                onChange={(event) =>
                  setForm({ ...form, safetyRepId: event.target.value ? Number(event.target.value) : null })
                }
              >
                <Select.Option value="">{t(`${K}.chooseSafetyRep`)}</Select.Option>
                {/* Shown as it is, but cannot be chosen again */}
                {plan && plan.safetyRepHasRole === false && plan.safetyRepEmail && !safetyRepIds.includes(plan.safetyRepId) && (
                  <Select.Option value={plan.safetyRepId} disabled>
                    {plan.safetyRepEmail}
                  </Select.Option>
                )}
                {options.safetyReps.map((rep) => (
                  <Select.Option key={rep.userId} value={rep.userId}>
                    {rep.email}
                  </Select.Option>
                ))}
              </Select>
              {errorFor(PLAN_FIELD_IDS.safetyRep) && (
                <ValidationMessage>{errorFor(PLAN_FIELD_IDS.safetyRep)}</ValidationMessage>
              )}
              {safetyRepWithoutRole && (
                <Alert data-color="warning" data-size="sm">
                  <Paragraph data-size="sm">{t(`${K}.safetyRepWithoutRole`, { email: safetyRepWithoutRole })}</Paragraph>
                </Alert>
              )}
              {options.safetyReps.length === 0 && (
                <Alert data-color="info" data-size="sm">
                  <Paragraph data-size="sm">
                    {t(`${K}.noSafetyReps`)} {isHr ? t(`${K}.noSafetyRepsHr`) : t(`${K}.noSafetyRepsAskHr`)}
                  </Paragraph>
                  {isHr && (
                    <Link asChild data-size="sm">
                      <RouterLink to={Routes.HRSafetyRepsFullPath}>{t(`${K}.noSafetyRepsLink`)}</RouterLink>
                    </Link>
                  )}
                </Alert>
              )}
              {form.safetyRepId !== null && form.safetyRepId === options.onlyGeneralManagerId && (
                <Alert data-color="warning" data-size="sm">
                  <Paragraph data-size="sm">{t(`${K}.onlyGeneralManager`)}</Paragraph>
                </Alert>
              )}
            </Field>
          )}
        </section>

        <section aria-labelledby="plan-when" className={layout.card} data-gap="md">
          <Heading level={2} data-size="sm" id="plan-when">
            {t(`${K}.when`)}
          </Heading>
          <Field>
            <Label htmlFor="plan-frequency">{t(`${K}.frequency`)}</Label>
            <Select
              id="plan-frequency"
              value={form.frequency}
              onChange={(event) => setForm({ ...form, frequency: event.target.value as PlanForm['frequency'] })}
            >
              {FREQUENCY_OPTIONS.map((frequency) => (
                <Select.Option key={frequency} value={frequency}>
                  {t(`safetyRounds.plans.frequency.${frequency}`)}
                </Select.Option>
              ))}
            </Select>
          </Field>
          <Field>
            <Label htmlFor={PLAN_FIELD_IDS.nextDate}>{t(`${K}.nextDate`)}</Label>
            <Paragraph data-size="sm" className={layout.subtle} id="plan-next-date-description">
              {t(`${K}.nextDateDescription`)}
            </Paragraph>
            <Input
              id={PLAN_FIELD_IDS.nextDate}
              type="date"
              value={form.nextDate}
              aria-describedby="plan-next-date-description"
              aria-invalid={errorFor(PLAN_FIELD_IDS.nextDate) ? true : undefined}
              onChange={(event) => setForm({ ...form, nextDate: event.target.value })}
            />
            {errorFor(PLAN_FIELD_IDS.nextDate) && (
              <ValidationMessage>{errorFor(PLAN_FIELD_IDS.nextDate)}</ValidationMessage>
            )}
          </Field>
          <Fieldset>
            <Fieldset.Legend>{t(`${K}.notice`)}</Fieldset.Legend>
            <Fieldset.Description>{t(`${K}.noticeDescription`)}</Fieldset.Description>
            {options.noticeDays.map((days) => (
              <Radio
                key={days}
                name="plan-notice"
                label={t(`${K}.noticeDays`, { count: days })}
                value={String(days)}
                checked={form.noticeDays === days}
                onChange={() => setForm({ ...form, noticeDays: days })}
              />
            ))}
          </Fieldset>
        </section>

        <section aria-labelledby="plan-who" className={layout.card} data-gap="md">
          <Heading level={2} data-size="sm" id="plan-who">
            {t(`${K}.who`)}
          </Heading>
          <Fieldset>
            <Fieldset.Legend>{t(`${K}.participants`)}</Fieldset.Legend>
            {options.participantRoles.map((role) => {
              const locked = options.lockedParticipants.includes(role);
              return (
                <Checkbox
                  key={role}
                  label={
                    <span className={classes.participant}>
                      {t(`safetyRounds.plans.roles.${role}`)}
                      {locked && <PadlockLockedIcon aria-hidden="true" />}
                    </span>
                  }
                  description={t(`${K}.roleDescriptions.${role}`)}
                  value={role}
                  checked={form.participants.includes(role)}
                  readOnly={locked}
                  onChange={() => setForm(toggleParticipant(form, role, options.lockedParticipants))}
                />
              );
            })}
          </Fieldset>
        </section>

        {errors && errors.length > 0 && (
          <ErrorSummary ref={summaryRef}>
            <ErrorSummary.Heading>{t(`${K}.errorSummary`)}</ErrorSummary.Heading>
            <ErrorSummary.List>
              {errors.map((error) => (
                <ErrorSummary.Item key={error.fieldId}>
                  <ErrorSummary.Link href={`#${error.fieldId}`}>{error.message}</ErrorSummary.Link>
                </ErrorSummary.Item>
              ))}
            </ErrorSummary.List>
          </ErrorSummary>
        )}
        {save.isError && (
          <Alert data-color="danger" data-size="sm">
            <Paragraph data-size="sm">
              {isSafetyRepWithoutRole(save.error) ? t(`${K}.saveSafetyRepWithoutRole`) : t('hms.errors.save')}
            </Paragraph>
          </Alert>
        )}
        <div className={layout.actions}>
          <Button data-size="sm" onClick={submit} aria-busy={save.isPending || undefined}>
            {t(`${K}.save`)}
          </Button>
          <Button asChild data-size="sm" variant="tertiary">
            <RouterLink to={home}>{t('safetyRounds.cancel')}</RouterLink>
          </Button>
        </div>
      </div>
      {plan && plan.mayEdit && (
        <aside className={layout.side}>
          <section aria-labelledby="plan-archive" className={layout.sideCard} data-gap="sm">
            <Heading level={2} data-size="2xs" id="plan-archive">
              {plan.active ? t(`${K}.archiveTitle`) : t(`${K}.unarchiveTitle`)}
            </Heading>
            <Paragraph data-size="sm">{plan.active ? t(`${K}.archiveText`) : t(`${K}.unarchiveText`)}</Paragraph>
            <Button
              data-size="sm"
              variant="secondary"
              onClick={() => archive.mutate(!plan.active, { onSuccess: () => navigate(home) })}
            >
              {plan.active ? t(`${K}.archive`) : t(`${K}.unarchive`)}
            </Button>
          </section>
        </aside>
      )}
    </div>
  );
};
